import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FaCheckCircle, FaKey } from "react-icons/fa";
import { toast } from "react-hot-toast";
import profilepic from "../../assests/modalpic.png";
import userService from "../../api/services/userService"; // 👈 import your API service
import { getAdminPanelRole } from "../../utils/adminPanelRole";

const tabs = [
  { key: "profile", label: "Profile" },
  { key: "send", label: "Send Request" },
  { key: "received", label: "Got Request" },
  { key: "favourites", label: "Favourites" },
  { key: "blocked", label: "Block User" },
  // { key: "packages", label: "Purchase Packages" },
  { key: "documents", label: "Documents" },
];

const SectionCard = ({ title, children, className = "" }) => (
  <div
    className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 ${className}`}>
    <h3 className="text-2xl font-bold text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-[15px] font-semibold text-slate-800">{label}</span>
    <span className="text-[15px] text-slate-500">{value ?? "--"}</span>
  </div>
);

const UserDetails = () => {
  const { id } = useParams();
  useLocation(); // keep hook in case needed later; remove unused `state`
  const [active, setActive] = useState("profile");
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [user, setUser] = useState(null);
  const PAGE_SIZE = 10;

  const adminRoleKey = getAdminPanelRole();
  const canResetPassword =
    adminRoleKey === "admin" || adminRoleKey === "developer";

  const closeResetPasswordModal = useCallback(() => {
    setResetPasswordOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }, []);

  const generateRandomPassword = useCallback((length = 12) => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
    const bytes =
      typeof crypto !== "undefined" && crypto.getRandomValues
        ? crypto.getRandomValues(new Uint32Array(length))
        : Array.from({ length }, () => Math.floor(Math.random() * 0xffffffff));
    let out = "";
    for (let i = 0; i < length; i++) {
      out += chars[bytes[i] % chars.length];
    }
    return out;
  }, []);

  const handleGeneratePassword = useCallback(() => {
    const pwd = generateRandomPassword(12);
    setNewPassword(pwd);
    setConfirmPassword(pwd);
    setShowNewPassword(true);
    setShowConfirmPassword(true);
    toast.success("Password generated");
  }, [generateRandomPassword]);

  const handleResetPassword = useCallback(async () => {
    const pwd = String(newPassword || "").trim();
    const confirm = String(confirmPassword || "").trim();
    if (!pwd) {
      toast.error("Password is required");
      return;
    }
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      setResettingPassword(true);
      const res = await userService.updateUserPassword(id, pwd);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to reset password");
        return;
      }
      toast.success(res?.message || "Password reset successfully");
      closeResetPasswordModal();
    } catch (err) {
      toast.error(err?.message || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  }, [id, newPassword, confirmPassword, closeResetPasswordModal]);

  const [sentPage, setSentPage] = useState(1);
  const [receivedPage, setReceivedPage] = useState(1);
  const [favPage, setFavPage] = useState(1);
  const [blockedPage, setBlockedPage] = useState(1);

  // Format date (match subscription start date format: date only)
  const formatDate = (dateString) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString();
  };

  const formatBool = (val) => {
    if (val === 1 || val === true) return "Yes";
    if (val === 0 || val === false) return "No";
    return val ?? "--";
  };

  // userdocument schema differs across DBs; use fallbacks
  const getDocUrl = (doc) =>
    doc?.documentUrl ||
    doc?.document ||
    doc?.url ||
    doc?.imageUrl ||
    doc?.frontUrl ||
    doc?.backUrl ||
    doc?.frontImage ||
    doc?.backImage ||
    doc?.cnicFront ||
    doc?.cnicBack ||
    doc?.fileUrl ||
    doc?.file ||
    null;

  const getDocType = (doc) =>
    doc?.documentType || doc?.type || doc?.docType || doc?.name || "Document";

  const getDocStatus = (doc) =>
    doc?.status ||
    (doc?.isApproved === 1 ? "APPROVED" : null) ||
    (doc?.isRejected === 1 ? "REJECTED" : null) ||
    (doc?.verified === 1 ? "APPROVED" : null) ||
    "PENDING";

  // Fetch user details from API
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await userService.detailsUser(id);
        setUser(res.details);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, [id]);

  // Reset tab pagination when user changes or tab changes
  useEffect(() => {
    setSentPage(1);
    setReceivedPage(1);
    setFavPage(1);
    setBlockedPage(1);
  }, [id, active]);

  const tabButtonClasses = (key) =>
    `whitespace-nowrap px-6 py-3 rounded-2xl text-sm font-semibold transition
     ${
       active === key
         ? "bg-red-500 text-white shadow-[0_10px_24px_rgba(239,68,68,0.45)]"
         : "text-slate-700 hover:text-red-500"
     }`;

  const ProfileTab = useMemo(
    () =>
      user && (
        <div className="bg-white rounded-3xl shadow-md border border-slate-100">
          <div className="p-5 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left: Avatar + Verify */}
              <div className="lg:col-span-4">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col items-center">
                  {user.imageUrl ? (
                    <img
                      src={user.imageUrl}
                      alt="User"
                      className="w-56 h-56 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-56 h-56 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 text-6xl select-none">
                      <span className="translate-y-1">👤</span>
                    </div>
                  )}

                  <button
                    onClick={() => setVerifyOpen(true)}
                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-500 text-white font-semibold shadow-[0_10px_24px_rgba(239,68,68,0.45)] hover:bg-red-600">
                    Verify Profile
                    <FaCheckCircle className="opacity-90" />
                  </button>

                  {canResetPassword ? (
                    <button
                      type="button"
                      onClick={() => setResetPasswordOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-800 text-white font-semibold shadow-md hover:bg-slate-900">
                      Reset Password
                      <FaKey className="opacity-90" />
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Right: scrollable detail cards */}
              <div className="lg:col-span-8">
                <div className="max-h-[600px] overflow-y-auto pr-1 lg:pr-2 custom-scroll">
                  {/* Basic Details full width row */}
                  <div className="">
                    <SectionCard title="Basic Details">
                      <div className="grid sm:grid-cols-2 gap-x-8">
                        <KV label="User ID" value={`#${user.id}`} />
                        <KV
                          label="Name"
                          value={`${user.firstName} ${user.lastName}`}
                        />
                        <KV label="Email" value={user.email} />
                        <KV label="Phone" value={user.contactNo} />

                        <KV
                          label="Created Date"
                          value={formatDate(user.createdDate)}
                        />
                        <KV
                          label="Updated Date"
                          value={formatDate(user.modifiedDate)}
                        />

                        <KV label="Active" value={formatBool(user.isActive)} />
                        <KV
                          label="Profile Complete"
                          value={formatBool(user.isProfileComplete)}
                        />
                        <KV
                          label="Verified"
                          value={formatBool(user.isVerified)}
                        />
                        <KV
                          label="Verify Profile Pic"
                          value={formatBool(user.isVerifyProfilePic)}
                        />
                        <KV
                          label="Disabled"
                          value={formatBool(user.isDisable)}
                        />
                        <KV label="Blur" value={formatBool(user.isBlur)} />
                        <KV
                          label="Completion %"
                          value={
                            user.percentage !== undefined &&
                            user.percentage !== null
                              ? `${user.percentage}%`
                              : "--"
                          }
                        />

                        <KV label="Religion" value={user.religion} />
                        <KV label="Sect" value={user.sect} />
                        <KV label="Parenting" value={user.parenting} />
                        <KV label="Marriage Plan" value={user.marriage} />
                        <KV label="Smoking" value={user.smoking} />
                        <KV label="Alcohol" value={user.alcohol} />
                      </div>
                    </SectionCard>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mt-5">
                    <SectionCard title="Personal Details">
                      <KV label="Marital Status" value={user.maritalStatus} />
                      <KV label="Height" value={user.height} />
                      <KV label="Age" value={user.age} />
                      <KV label="Languages" value={user.languages} />
                      <KV label="Diet" value={user.diet} />
                      <KV
                        label="Children"
                        value={
                          user.childerns === 0
                            ? "Does not have Childern"
                            : "Have Childern"
                        }
                      />
                    </SectionCard>

                    <SectionCard title="Education & Career Details">
                      <KV label="Education" value={user.education} />
                      <KV label="Occupation" value={user.occupation} />
                      <KV label="Monthly Income" value={user.monthlyIncome} />
                      <KV label="Career Status" value={user.careerStatus} />
                    </SectionCard>

                    <SectionCard
                      title="Address Details"
                      className="md:col-span-2">
                      <KV label="Address" value={user.address} />
                      <KV label="Grew Up" value={user.grewUp} />
                      <KV label="Residential" value={user.residential} />
                    </SectionCard>
                  </div>

                  {/* About Me */}
                  <div className="mt-5">
                    <SectionCard title="About Me">
                      <p className="text-slate-600">{user.aboutMe}</p>
                    </SectionCard>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Verify Modal */}
          {verifyOpen && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl relative">
                <button
                  className="absolute top-3 right-3 text-slate-500 hover:text-slate-800"
                  onClick={() => setVerifyOpen(false)}>
                  ✕
                </button>
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">
                    Verify User Profile Pic
                  </h2>
                  <div className="w-full flex items-center justify-center mb-6">
                    <img
                      src={user.imageUrl || profilepic}
                      alt="profile"
                      className="rounded-lg max-h-72 object-contain"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setVerifyOpen(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
                      Approve
                    </button>
                    <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reset Password Modal — Admin / Developer only */}
          {resetPasswordOpen && canResetPassword && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-md rounded-2xl shadow-xl relative">
                <button
                  type="button"
                  className="absolute top-3 right-3 text-slate-500 hover:text-slate-800"
                  onClick={closeResetPasswordModal}
                  disabled={resettingPassword}>
                  ✕
                </button>
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-1">Reset Password</h2>
                  <p className="text-sm text-slate-500 mb-5">
                    Set a new password for{" "}
                    <span className="font-semibold text-slate-700">
                      {user.firstName} {user.lastName}
                    </span>{" "}
                    (#{user.id})
                  </p>

                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      disabled={resettingPassword}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-800 text-sm font-semibold border border-slate-200 hover:bg-slate-200 disabled:opacity-60">
                      Auto Generate Password
                    </button>
                  </div>

                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    New Password
                  </label>
                  <div className="relative mb-4">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      autoComplete="new-password"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                      disabled={resettingPassword}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 hover:text-slate-800"
                      onClick={() => setShowNewPassword((v) => !v)}>
                      {showNewPassword ? "Hide" : "Show"}
                    </button>
                  </div>

                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative mb-6">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      autoComplete="new-password"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                      disabled={resettingPassword}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleResetPassword();
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 hover:text-slate-800"
                      onClick={() => setShowConfirmPassword((v) => !v)}>
                      {showConfirmPassword ? "Hide" : "Show"}
                    </button>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeResetPasswordModal}
                      disabled={resettingPassword}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-60">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={resettingPassword}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-60 font-semibold">
                      {resettingPassword ? "Saving…" : "Reset Password"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    [
      user,
      verifyOpen,
      resetPasswordOpen,
      canResetPassword,
      newPassword,
      confirmPassword,
      showNewPassword,
      showConfirmPassword,
      resettingPassword,
      handleGeneratePassword,
      handleResetPassword,
      closeResetPasswordModal,
    ]
  );

  // Other tabs
  const TabShell = ({ children }) => (
    <div className="bg-white rounded-3xl shadow-md border border-slate-100 p-6">
      {children}
    </div>
  );

  const Pagination = ({ page, setPage, totalItems }) => {
    const totalPages = Math.max(1, Math.ceil((totalItems || 0) / PAGE_SIZE));
    if ((totalItems || 0) <= PAGE_SIZE) return null;
    return (
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-xs md:text-sm font-medium text-gray-700">
          Showing page <span className="font-bold text-gray-900">{page}</span>{" "}
          of <span className="font-bold text-gray-900">{totalPages}</span>{" "}
          (Total: <span className="font-bold text-red-600">{totalItems}</span>{" "}
          records)
        </div>
        <div className="flex gap-2 items-center justify-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
              page === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
            }`}>
            Previous
          </button>
          <span className="text-xs md:text-sm text-gray-700">
            <span className="font-bold">{(page - 1) * 10 + 1}</span> -{" "}
            <span className="font-bold">{Math.min(page * 10, totalItems)}</span>{" "}
            of <span className="font-bold text-red-600">{totalItems}</span>
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
              page === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
            }`}>
            Next
          </button>
        </div>
      </div>
    );
  };

  const UserCard = ({ user, status, date }) => (
    <div className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition">
      <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
        {user?.imageUrl ? (
          <img
            src={user.imageUrl}
            alt={user.firstName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-400 text-2xl">
            👤
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-slate-800 truncate">
          {user?.firstName} {user?.lastName}
        </h4>
        <p className="text-sm text-slate-500 truncate">{user?.email}</p>
        {user?.occupation && (
          <p className="text-xs text-slate-400 mt-1">{user.occupation}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        {status && (
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
              status === "ACCEPTED"
                ? "bg-green-100 text-green-700"
                : status === "REJECTED"
                ? "bg-red-100 text-red-700"
                : status === "CANCELLED"
                ? "bg-gray-100 text-gray-700"
                : "bg-blue-100 text-blue-700"
            }`}>
            {status}
          </span>
        )}
        {date && (
          <p className="text-xs text-slate-400 mt-1">{formatDate(date)}</p>
        )}
      </div>
    </div>
  );

  const SendRequestTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">
        Send Request ({user?.sentProposals?.length || 0})
      </h3>
      {user?.sentProposals && user.sentProposals.length > 0 ? (
        <div className="space-y-3">
          {user.sentProposals
            .slice((sentPage - 1) * PAGE_SIZE, sentPage * PAGE_SIZE)
            .map((proposal) => {
              const status =
                proposal.hascancelled === 1
                  ? "CANCELLED"
                  : proposal.status === 1
                  ? "ACCEPTED"
                  : proposal.status === 0
                  ? "REJECTED"
                  : "PENDING";
              return (
                <UserCard
                  key={proposal.id}
                  user={{
                    firstName: proposal.firstName,
                    lastName: proposal.lastName,
                    email: proposal.email,
                    imageUrl: proposal.imageUrl,
                    occupation: proposal.occupation,
                  }}
                  status={status}
                  date={proposal.createdDate}
                />
              );
            })}
          <Pagination
            page={sentPage}
            setPage={setSentPage}
            totalItems={user.sentProposals.length}
          />
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">
          No sent requests found.
        </p>
      )}
    </TabShell>
  );

  const GotRequestTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">
        Got Request ({user?.receivedProposals?.length || 0})
      </h3>
      {user?.receivedProposals && user.receivedProposals.length > 0 ? (
        <div className="space-y-3">
          {user.receivedProposals
            .slice((receivedPage - 1) * PAGE_SIZE, receivedPage * PAGE_SIZE)
            .map((proposal) => {
              const status =
                proposal.hascancelled === 1
                  ? "CANCELLED"
                  : proposal.status === 1
                  ? "ACCEPTED"
                  : proposal.status === 0
                  ? "REJECTED"
                  : "PENDING";
              return (
                <UserCard
                  key={proposal.id}
                  user={{
                    firstName: proposal.firstName,
                    lastName: proposal.lastName,
                    email: proposal.email,
                    imageUrl: proposal.imageUrl,
                    occupation: proposal.occupation,
                  }}
                  status={status}
                  date={proposal.createdDate}
                />
              );
            })}
          <Pagination
            page={receivedPage}
            setPage={setReceivedPage}
            totalItems={user.receivedProposals.length}
          />
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">
          No received requests found.
        </p>
      )}
    </TabShell>
  );

  const FavouritesTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">
        Favourites ({user?.favourites?.length || 0})
      </h3>
      {user?.favourites && user.favourites.length > 0 ? (
        <div className="space-y-3">
          {user.favourites
            .slice((favPage - 1) * PAGE_SIZE, favPage * PAGE_SIZE)
            .map((fav) => (
              <UserCard
                key={fav.id}
                user={{
                  firstName: fav.firstName,
                  lastName: fav.lastName,
                  email: fav.email,
                  imageUrl: fav.imageUrl,
                  occupation: fav.occupation,
                }}
              />
            ))}
          <Pagination
            page={favPage}
            setPage={setFavPage}
            totalItems={user.favourites.length}
          />
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">
          No favourite users found.
        </p>
      )}
    </TabShell>
  );

  const BlockedTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">
        Block User ({user?.blockedUsers?.length || 0})
      </h3>
      {user?.blockedUsers && user.blockedUsers.length > 0 ? (
        <div className="space-y-3">
          {user.blockedUsers
            .slice((blockedPage - 1) * PAGE_SIZE, blockedPage * PAGE_SIZE)
            .map((blocked) => (
              <UserCard
                key={blocked.block}
                user={{
                  firstName: blocked.firstName,
                  lastName: blocked.lastName,
                  imageUrl: blocked.imageUrl,
                }}
              />
            ))}
          <Pagination
            page={blockedPage}
            setPage={setBlockedPage}
            totalItems={user.blockedUsers.length}
          />
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">
          No blocked users found.
        </p>
      )}
    </TabShell>
  );

  // const PackagesTab = (
  //   <TabShell>
  //     <h3 className="text-xl font-bold text-slate-800 mb-4">Purchase Packages</h3>
  //     <p className="text-slate-500">Packages purchased by user will show here</p>
  //   </TabShell>
  // );

  const DocumentsTab = (
    <TabShell>
      <h3 className="text-xl font-bold text-slate-800 mb-4">
        Documents ({user?.documents?.length || 0})
      </h3>
      {user?.documents && user.documents.length > 0 ? (
        <div className="space-y-3">
          {user.documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition">
              <div className="flex-1">
                <h4 className="font-semibold text-slate-800">
                  {getDocType(doc)}
                </h4>
                <p className="text-sm text-slate-500 mt-1">
                  Uploaded: {formatDate(doc.createdDate)}
                </p>
                {(doc.updatedDate || doc.modifiedDate || doc.updated_at) && (
                  <p className="text-xs text-slate-400 mt-1">
                    Updated:{" "}
                    {formatDate(
                      doc.updatedDate || doc.modifiedDate || doc.updated_at
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    getDocStatus(doc) === "APPROVED"
                      ? "bg-green-100 text-green-700"
                      : getDocStatus(doc) === "REJECTED"
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}>
                  {getDocStatus(doc)}
                </span>
                {getDocUrl(doc) && (
                  <a
                    href={getDocUrl(doc)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-sm font-semibold">
                    View
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">No documents found.</p>
      )}
    </TabShell>
  );

  const renderTab = () => {
    switch (active) {
      case "profile":
        return ProfileTab;
      case "send":
        return SendRequestTab;
      case "received":
        return GotRequestTab;
      case "favourites":
        return FavouritesTab;
      case "blocked":
        return BlockedTab;
      // case "packages": return PackagesTab;
      case "documents":
        return DocumentsTab;
      default:
        return ProfileTab;
    }
  };

  return (
    <div className="p-4 md:p-6 w-full">
      {/* Tabs container */}
      <div className="bg-white rounded-3xl shadow-md border border-slate-100">
        <div className="px-4 md:px-6 pt-4">
          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-3">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={tabButtonClasses(t.key)}
                onClick={() => setActive(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-px bg-slate-100" />
      </div>

      {/* Tab content */}
      <div className="mt-4">{renderTab()}</div>
    </div>
  );
};

export default UserDetails;
